import { useState } from 'react'
import { Check, Copy, Maximize2, TriangleAlert } from 'lucide-react'
import { Button } from '#/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '#/components/ui/dialog'
import { m } from '#/paraglide/messages'
import { cn } from '#/lib/utils'

export function useCopy() {
  const [copied, setCopied] = useState(false)
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
      return true
    } catch {
      return false
    }
  }
  return { copied, copy }
}

/** Mono prompt block with expand + copy, fade at the bottom, warning line (spec/05 §4). */
export function PromptBlock({
  text,
  warning,
  onCopy,
  className,
  maxHeight = 220,
}: {
  text: string
  warning?: string
  onCopy?: () => void
  className?: string
  maxHeight?: number
}) {
  const { copied, copy } = useCopy()
  const [open, setOpen] = useState(false)
  const doCopy = async () => {
    if (await copy(text)) onCopy?.()
  }
  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative rounded-lg border bg-muted/50">
        <pre className="overflow-hidden whitespace-pre-wrap break-words p-3 pr-24 font-mono text-[13px] leading-relaxed" style={{ maxHeight }}>
          {text}
        </pre>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-lg bg-gradient-to-t from-muted/90 to-transparent" />
        <div className="absolute right-2 top-2 flex gap-1">
          <Button size="icon" variant="ghost" className="size-8 bg-background/70" onClick={() => setOpen(true)} aria-label={m.common_expand()}>
            <Maximize2 className="size-4" />
          </Button>
          <Button size="sm" className="h-8" onClick={doCopy}>
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? m.common_copied() : m.common_copy()}
          </Button>
        </div>
      </div>
      {warning ? (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-status-progress" /> {warning}
        </p>
      ) : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
          <DialogTitle className="sr-only">Prompt</DialogTitle>
          <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-muted/50 p-4 font-mono text-[13px] leading-relaxed">{text}</pre>
          <div className="flex justify-end">
            <Button onClick={doCopy}>
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              {copied ? m.common_copied() : m.common_copy()}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
